import Axios from "axios";

export default {
  /**
   * Récupère la liste des types de dépenses
   * @param {string} clientCode - Code client
   * @param {string} typeRegime - Type de régime (obligatoire)
   * @returns {Promise<Array|null>} Liste des types de dépenses ou null en cas d'erreur
   */
  async getTypesDepenses(clientCode, typeRegime) {
    try {
      const params = new URLSearchParams();
      params.append("clientCode", clientCode);
      params.append("typeRegime", typeRegime);
      const response = await Axios.get(`/api/simulateur/depenses?${params.toString()}`);
      return response || null;
    } catch (error) {
      console.error("Erreur lors de la récupération des types de dépenses:", error);
      return null;
    }
  },

  /**
   * Récupère la liste des prestations pour un type de dépense donné
   * @param {string} clientCode - Code client
   * @param {string} typeRegime - Type de régime
   * @param {number} typeDepense - L'identifiant du type de dépense
   * @returns {Promise<Array|null>} Liste des prestations ou null en cas d'erreur
   */
  async getPrestations(clientCode, typeRegime, typeDepense) {
    try {
      const params = new URLSearchParams();
      params.append("clientCode", clientCode);
      params.append("typeRegime", typeRegime);
      params.append("typeDepense", typeDepense);
      const response = await Axios.get(`/api/simulateur/prestations?${params.toString()}`);
      return response || null;
    } catch (error) {
      console.error("Erreur lors de la récupération des prestations:", error);
      return null;
    }
  },

  /**
   * Récupère la configuration d'une prestation (champs de saisie)
   * @param {string} clientCode - Code client
   * @param {string} typeRegime - Type de régime
   * @param {number} typeDepense - Type de dépense
   * @param {number} typePrestation - Type de prestation
   * @param {number} typeRemboursement - Type de remboursement
   * @returns {Promise<Object|null>} Configuration de la prestation ou null en cas d'erreur
   *
   * Exemple de retour:
   * {
   *   estComposite: true,
   *   montantMaximal: 500.0, // Plafond global pour prestation simple
   *   champsMontants: [
   *     { code: "Monture", label: "Montant de la monture", placeholder: "0.00", montantMaximal: 120.0, ordre: 1 },
   *     { code: "Verres", label: "Montant des verres", placeholder: "0.00", montantMaximal: 300.0, ordre: 2 }
   *   ]
   * }
   */
  async getConfigurationPrestation(clientCode, typeRegime, typeDepense, typePrestation, typeRemboursement) {
    try {
      const params = new URLSearchParams();
      params.append("clientCode", clientCode);
      params.append("typeRegime", typeRegime);
      params.append("typeDepense", typeDepense);
      params.append("typePrestation", typePrestation);
      if (typeRemboursement !== null && typeRemboursement !== undefined) {
        params.append("typeRemboursement", typeRemboursement);
      }

      const response = await Axios.get(`/api/simulateur/configuration-prestation?${params.toString()}`);
      return response || null;
    } catch (error) {
      console.error("Erreur lors de la récupération de la configuration de prestation:", error);
      return null;
    }
  },

  /**
   * Récupère la liste des types de remboursement
   * @param {string} clientCode - Code client
   * @param {string} typeRegime - Type de régime
   * @returns {Promise<Array|null>} Liste des types de remboursement ou null en cas d'erreur
   */
  async getTypesRemboursement(clientCode, typeRegime) {
    try {
      const params = new URLSearchParams();
      params.append("clientCode", clientCode);
      params.append("typeRegime", typeRegime);
      const response = await Axios.get(`/api/simulateur/remboursements?${params.toString()}`);
      return response || null;
    } catch (error) {
      console.error("Erreur lors de la récupération des types de remboursement:", error.message);
      return null;
    }
  },

  /**
   * Simule un remboursement santé
   * @param {string} clientCode - Code client
   * @param {string} typeRegime - Type de régime
   * @param {Object} simulationData - Les données de simulation
   * @param {number} simulationData.TypeDepense - Type de dépense
   * @param {number} simulationData.TypePrestation - Type de prestation
   * @param {number} simulationData.TypeRemboursement - Type de remboursement
   * @param {number} [simulationData.MontantDepense] - Montant de la dépense (pour prestation simple)
   * @param {Array} [simulationData.SousPrestations] - Liste des sous-prestations (pour prestation composite)
   * @returns {Promise<Object|null>} Résultat de la simulation ou null en cas d'erreur
   */
  async simulerRemboursement(clientCode, typeRegime, simulationData) {
    try {
      const payload = {
        ClientCode: clientCode,
        TypeRegime: typeRegime,
        TypeDepense: simulationData.TypeDepense,
        TypePrestation: simulationData.TypePrestation,
      };

      // Ajouter TypeRemboursement seulement s'il a une valeur valide
      if (simulationData.TypeRemboursement !== null && simulationData.TypeRemboursement !== undefined) {
        payload.TypeRemboursement = simulationData.TypeRemboursement;
      }

      // Ajouter soit MontantDepense soit SousPrestations
      if (simulationData.SousPrestations) {
        payload.SousPrestations = simulationData.SousPrestations;
      } else {
        payload.MontantDepense = simulationData.MontantDepense;
      }

      const response = await Axios.post("/api/simulateur/simuler-remboursement", payload);
      return response || null;
    } catch (error) {
      console.error("Erreur lors de la simulation de remboursement:", error);
      return null;
    }
  },
};
